import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material'
import { GreenText } from 'components'
import {
  BuyerExporterCompanyDto,
  OutboundTripDestinationAddressDto,
  OutboundTripDestinationCompanyDto,
  OutboundTripDetailDestinationDto
} from 'dtos/outboundTrips'
import { Form, Formik, getIn } from 'formik'
import { formatAddress } from 'utils'
import { OutboundTripDetailDestinationDtoValidator } from 'validators/outboundTrips'

interface DestinationDialogProps {
  buyerExporterCompanies: BuyerExporterCompanyDto[]
  initialValues: OutboundTripDetailDestinationDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: OutboundTripDetailDestinationDto) => void
  open: boolean
}

export default function DestinationDialog({ buyerExporterCompanies, initialValues, isLoading, onClose, onSave, open }: DestinationDialogProps) {
  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => onSave(values)}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={OutboundTripDetailDestinationDtoValidator}
      >
        {({ errors, handleBlur, handleChange, setFieldValue, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Outbound Trip Destination</DialogTitle>

                  <Grid item xs={12}>
                    <Typography variant='body1'>Outbound Trip Destination</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(errors.destinationCompany)}
                      fullWidth
                      helperText={getIn(errors, 'destinationCompany.companyGuid')}
                      label='Buyer or Exporter Company'
                      onBlur={handleBlur}
                      onChange={e => {
                        const buyerExporterCompany = JSON.parse(e.target.value) as BuyerExporterCompanyDto

                        setFieldValue('destinationCompany', {
                          companyGuid: buyerExporterCompany.companyGuid,
                          name: buyerExporterCompany.name
                        } as OutboundTripDestinationCompanyDto)
                      }}
                      select
                      value={
                        values.destinationCompany && buyerExporterCompanies.filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0]
                          ? JSON.stringify(buyerExporterCompanies.filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0])
                          : null
                      }
                    >
                      {buyerExporterCompanies.map(c => (
                        <MenuItem key={c.companyGuid} value={JSON.stringify(c)}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body1'>Outbound Trip Destination Address</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors, 'destinationCompanyAddress.addressGuid'))}
                      fullWidth
                      helperText={getIn(errors, 'destinationCompanyAddress.addressGuid')}
                      label='Vehicle Destination Address'
                      onBlur={handleBlur}
                      onChange={e => {
                        const vehicleDestinationAddress = JSON.parse(e.target.value) as OutboundTripDestinationAddressDto

                        setFieldValue('destinationCompanyAddress', vehicleDestinationAddress)
                        setFieldValue('destinationAddress', {
                          ...vehicleDestinationAddress,
                          addressGuid: undefined,
                          county: vehicleDestinationAddress.county ?? ''
                        })
                      }}
                      select
                      value={
                        values.destinationCompany?.companyGuid &&
                        values.destinationCompanyAddress?.addressGuid &&
                        buyerExporterCompanies.filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0] &&
                        buyerExporterCompanies
                          .filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0]
                          .vehicleDestinationAddresses.filter(a => a.addressGuid === values.destinationCompanyAddress?.addressGuid)[0]
                          ? JSON.stringify(
                              buyerExporterCompanies
                                .filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0]
                                .vehicleDestinationAddresses.filter(a => a.addressGuid === values.destinationCompanyAddress?.addressGuid)[0]
                            )
                          : null
                      }
                    >
                      {(values.destinationCompany?.companyGuid &&
                      buyerExporterCompanies.filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0]
                        ? buyerExporterCompanies.filter(c => c.companyGuid === values.destinationCompany?.companyGuid)[0].vehicleDestinationAddresses
                        : []
                      ).map(a => (
                        <MenuItem key={a.addressGuid} value={JSON.stringify(a)}>
                          {`${a.addressNickname}, ${formatAddress(a, false)}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'addressNickname'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'addressNickname')}
                      label='Address Nickname'
                      name='destinationAddress.addressNickname'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.addressNickname ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'address1'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'address1')}
                      label='Address 1'
                      name='destinationAddress.address1'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.address1 ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'address2'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'address2')}
                      label='Address 2'
                      name='destinationAddress.address2'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.address2 ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'city'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'city')}
                      label='City'
                      name='destinationAddress.city'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.city ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'stateProvince'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'stateProvince')}
                      label='State / Province'
                      name='destinationAddress.stateProvince'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.stateProvince ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'county'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'county')}
                      label='County'
                      name='destinationAddress.county'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.county ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'postalCode'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'postalCode')}
                      label='Postal Code'
                      name='destinationAddress.postalCode'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.postalCode ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(getIn(errors.destinationAddress, 'country'))}
                      fullWidth
                      helperText={getIn(errors.destinationAddress, 'country')}
                      label='country'
                      name='destinationAddress.country'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.destinationAddress?.country ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Buyer or Exporter Company Drop Down Field:**

A drop-down field that contains all active companies in VTS2 marked as a Buyer and/or Exporter Company.

**Vehicle Destination Address Drop Down Field:**

A drop-down field that is populated after users select a company in the “Exporter or Buyer Company” dropdown with all “Vehicle Destination Address(es)” associated with the selected company. Addresses appear in this dropdown as “Address Nickname, Address 1, Address 2, City, State, Postal Code”.

**Address 1 Field:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**Address 2 Field:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**City Field:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**State/Province Field:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**County Field:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**Postal Code:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**Country:**

A free-text field that is pre-populated based on the Vehicle Destination Address that is selected.

User can change the address in this field for “One Off” destinations that will only be used once.

**Save Button:**

Clicking this button will save all values entered

**Cancel Button:**

Clicking the button will close the dialog and not save any values entered.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
